import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Save, ArrowLeft, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPreise = () => {
  const [preise, setPreise] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    features: [''],
    order: 0
  });
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPreise();
  }, []);

  const fetchPreise = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API}/preise`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreise(response.data);
    } catch (error) {
      console.error('Error fetching preise:', error);
      toast({
        title: "Fehler",
        description: "Preise konnten nicht geladen werden",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (preis) => {
    setEditingId(preis.id);
    setFormData({
      title: preis.title,
      description: preis.description,
      features: preis.features.length > 0 ? preis.features : [''],
      order: preis.order
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setAddingNew(false);
    setFormData({ title: '', description: '', features: [''], order: 0 });
  };

  const handleAddFeature = () => {
    setFormData({
      ...formData,
      features: [...formData.features, '']
    });
  };

  const handleRemoveFeature = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      features: newFeatures.length > 0 ? newFeatures : ['']
    });
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({
      ...formData,
      features: newFeatures
    });
  };

  const handleSave = async () => {
    const token = localStorage.getItem('authToken');
    const cleanedFeatures = formData.features.filter(f => f.trim() !== '');

    try {
      if (editingId) {
        await axios.put(
          `${API}/preise/${editingId}`,
          { ...formData, features: cleanedFeatures },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({
          title: "Gespeichert!",
          description: "Eintrag wurde aktualisiert"
        });
      } else {
        await axios.post(
          `${API}/preise`,
          { ...formData, features: cleanedFeatures },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({
          title: "Erstellt!",
          description: "Neuer Eintrag wurde erstellt"
        });
      }

      handleCancelEdit();
      fetchPreise();
    } catch (error) {
      console.error('Error saving preis:', error);
      toast({
        title: "Fehler",
        description: error.response?.data?.detail || "Eintrag konnte nicht gespeichert werden",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (preisId, preisTitle) => {
    if (!window.confirm(`Moechten Sie den Eintrag "${preisTitle}" wirklich loeschen?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${API}/preise/${preisId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Geloescht",
        description: "Eintrag wurde erfolgreich geloescht"
      });

      fetchPreise();
    } catch (error) {
      console.error('Error deleting preis:', error);
      toast({
        title: "Fehler",
        description: error.response?.data?.detail || "Eintrag konnte nicht geloescht werden",
        variant: "destructive"
      });
    }
  };

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Wird geladen...</p>
      </div>
    );
  }

  const renderForm = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Titel *</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
          placeholder="z.B. Portrait-Shooting"
          data-testid="preis-title-input"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Beschreibung *</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none resize-none"
          rows="3"
          placeholder="Kurze Beschreibung, z.B. Preis pro Stunde/Paket"
          data-testid="preis-desc-input"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Features / Preisdetails</label>
        {formData.features.map((feature, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <input
              type="text"
              value={feature}
              onChange={(e) => handleFeatureChange(index, e.target.value)}
              className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
              placeholder="z.B. 30 Min Shooting - 89 €"
            />
            {formData.features.length > 1 && (
              <button
                type="button"
                onClick={() => handleRemoveFeature(index)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
              >
                <X size={20} />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={handleAddFeature}
          className="text-sm text-primary hover:underline"
        >
          + Feature hinzufuegen
        </button>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Sortierung</label>
        <input
          type="number"
          value={formData.order}
          onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
        />
      </div>
      <div className="flex gap-3">
        <button
          onClick={handleSave}
          data-testid="preis-save-btn"
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-gray-800 transition-colors"
        >
          <Save size={18} />
          {editingId ? 'Speichern' : 'Erstellen'}
        </button>
        <button
          onClick={handleCancelEdit}
          className="px-4 py-2 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Abbrechen
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="admin-preise-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/settings')}
            className="text-gray-600 hover:text-gray-900 mb-4 flex items-center gap-2"
          >
            <ArrowLeft size={20} />
            Zurueck zu Einstellungen
          </button>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-heading font-bold text-gray-900">Preise &amp; Leistungen</h1>
              <p className="text-gray-600 mt-2">Verwalten Sie Ihre Preise und Leistungspakete</p>
            </div>
            <button
              onClick={() => setAddingNew(true)}
              data-testid="preis-new-btn"
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              <Plus size={20} />
              Neuer Eintrag
            </button>
          </div>
        </div>

        {addingNew && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Neuen Eintrag erstellen</h2>
            {renderForm()}
          </div>
        )}

        <div className="space-y-4">
          {preise.map((preis) => (
            <div key={preis.id} className="bg-white rounded-lg shadow-md p-6">
              {editingId === preis.id ? (
                renderForm()
              ) : (
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2">{preis.title}</h3>
                    <p className="text-sm text-gray-600 mb-3">{preis.description}</p>
                    {preis.features.length > 0 && (
                      <ul className="list-disc list-inside text-sm text-gray-600 mb-2">
                        {preis.features.map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                      </ul>
                    )}
                    <p className="text-sm text-gray-500">Sortierung: {preis.order}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(preis)}
                      className="p-2 text-gray-600 hover:text-primary hover:bg-gray-100 rounded-lg transition-all"
                    >
                      <Edit2 size={20} />
                    </button>
                    <button
                      onClick={() => handleDelete(preis.id, preis.title)}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {preise.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500 mb-4">Noch keine Preise vorhanden</p>
            <button
              onClick={() => setAddingNew(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              <Plus size={20} />
              Ersten Eintrag erstellen
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPreise;
